import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { eachDayOfInterval } from 'date-fns';

import { AviaSearchParameters, DateWithPrice, Maybe } from '@websky/graphql';
import { StartSearch } from '../../../../types';
import { Money } from '../../../../../types';
import { API_DATE_FORMAT, format } from '../../../../../utils';
import { sortSegments } from '../utils';
import { useWeekCalendarMinPrices } from '@shared/lib/results';
import WeekCalendarBase from '../../../../../WeekCalendar';

interface Props {
	searchParameters: AviaSearchParameters;
	legId: number;
	startNewSearch?: StartSearch;
	alternativePrices?: DateWithPrice[];
	hidePrice?: boolean;
	minDate?: Date;
	allowSelectWithoutPrice?: boolean;
	avaliableDateRange?: [Maybe<Date>, Maybe<Date>];
	currentMinPriceSelectedDate?: Money;
	isRT?: boolean;
}

const WeekCalendar: React.FC<Props> = ({
	searchParameters,
	legId,
	startNewSearch,
	hidePrice,
	alternativePrices,
	minDate,
	allowSelectWithoutPrice,
	avaliableDateRange,
	currentMinPriceSelectedDate,
	isRT
}) => {
	const {
		calendarNavigationOffset,
		calendarParams,
		loading,
		getPrices,
		shiftCalendarPage
	} = useWeekCalendarMinPrices({ legId, searchParameters });

	const { centralDate, startDate, endDate } = calendarParams;

	const calendarPrices = useMemo(() => {
		const showAlternativePrices = legId === 0 && calendarNavigationOffset === 0 && alternativePrices;
		const prices = showAlternativePrices ? alternativePrices : getPrices(legId, isRT) ?? alternativePrices;

		const dateWithPriceMap = new Map(prices.map(dateWithPrice => [dateWithPrice.date, dateWithPrice]));
		const daysIntervalDates = eachDayOfInterval({ start: startDate, end: endDate });

		return daysIntervalDates.map(date => {
			const formatDate = format(date, API_DATE_FORMAT);
			const dateWithPrice = dateWithPriceMap.get(formatDate);

			return {
				date: format(date, API_DATE_FORMAT),
				price: dateWithPrice?.price ?? null
			};
		});
	}, [alternativePrices, legId, isRT, calendarNavigationOffset, startDate, endDate, getPrices]);

	const onDateClick = useCallback(
		(date: Date) => {
			const segments = [...searchParameters.segments];

			segments[legId] = {
				...segments[legId],
				date: format(date, API_DATE_FORMAT)
			};

			startNewSearch({
				...searchParameters,
				segments: sortSegments(legId, date, segments)
			});
		},
		[searchParameters, legId]
	);

	return (
		<WeekCalendarBase
			loading={loading}
			prices={calendarPrices}
			centralDate={centralDate}
			onSelect={onDateClick}
			onRangeChange={shiftCalendarPage}
			hidePrice={hidePrice}
			isMilesSearch={searchParameters?.ffpMode}
			minDate={minDate}
			allowSelectWithoutPrice={allowSelectWithoutPrice}
			avaliableDateRange={avaliableDateRange}
			currentMinPriceSelectedDate={currentMinPriceSelectedDate}
		/>
	);
};

export default WeekCalendar;
